import {FinancialDetailIcon} from '$assets/svgs';
import {eoCard} from '$modules/frontdesk/styles';
import {Grid, Box, Stack, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {RouteSection, getRoute} from '$utils/route';
import {RoutePath} from '$constants/routes';
import FrontdeskButton from '$components/FrontdeskButton';
import useAuthUser from '~/hooks/useAuthUser';
import {UserRoleName} from '$modules/admin/constant';

interface FinancialDetails {
  self_paying: boolean;
  insurance_claim: {
    insurance_id: string;
    insurer_name: string;
    membership_no: string;
    pre_authorisation_code?: string | undefined;
  };
  payment?: {
    id: number;
    payment_method: string;
  };
  payment_status?: string;
  appointment_id: number;
  appointment_status: string;
}

const FinancialDetails = (details: FinancialDetails) => {
  const {
    self_paying,
    insurance_claim,
    payment,
    payment_status,
    appointment_id,
    appointment_status,
  } = details;

  const navigate = useNavigate();
  const {roleName} = useAuthUser()!;
  const isFrontdesk = roleName === UserRoleName.Frontdesk;
  const isFinance = roleName === UserRoleName.Finance;

  const gradientText = {
    background: '-webkit-linear-gradient(180deg, #7625DC, #6063F2)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };

  const handlePaymentDetailsClick = () => {
    if (payment?.id) {
      if (isFinance) {
        navigate(
          getRoute(
            RouteSection.Finance,
            `${RoutePath.staff.finance.payments}/${payment.id}`
          )
        );
      } else {
        navigate(
          getRoute(
            RouteSection.Calendar,
            `${RoutePath.staff.calendar.bookingDetails}/payment/${payment.id}`
          ),
          {
            state: {
              backRoute: getRoute(
                RouteSection.Calendar,
                `${RoutePath.staff.calendar.bookingDetails}/${appointment_id}`
              ),
            },
          }
        );
      }
    }
  };

  return (
    <Grid item sx={{flex: 1}}>
      <Box sx={{...eoCard, height: '100%'}}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <FinancialDetailIcon />
            <Typography variant="h4" style={gradientText}>
              Financial details
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{mb: 1}}>
              Self paying
            </Typography>
            <Typography variant="subtitle2">
              {self_paying ? 'Yes' : ' No'}
            </Typography>
          </Box>
          {!self_paying ? (
            <>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1}}>
                  Insurance provider
                </Typography>
                <Typography variant="subtitle2">
                  {insurance_claim?.insurer_name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1}}>
                  Membership number
                </Typography>
                <Typography variant="subtitle2">
                  {insurance_claim?.membership_no || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1}}>
                  Pre-Authorisation Code
                </Typography>
                <Typography variant="subtitle2">
                  {insurance_claim?.pre_authorisation_code || '-'}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle1" sx={{mb: 1}}>
                  Payment mode
                </Typography>
                <Typography variant="subtitle2">
                  {payment?.payment_method || '-'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{mb: 1}}>
                    Payment status
                  </Typography>
                  {payment_status === 'paid' ? (
                    <Typography
                      variant="subtitle2"
                      className="payment-status-paid"
                    >
                      Paid
                    </Typography>
                  ) : payment_status === 'unpaid' ? (
                    <Typography
                      variant="subtitle2"
                      className="payment-status-unpaid"
                    >
                      Unpaid
                    </Typography>
                  ) : payment_status === 'refund_requested' ? (
                    <Typography variant="subtitle2">
                      Refund requested
                    </Typography>
                  ) : payment_status === 'refunded' ? (
                    <Typography variant="subtitle2">Refunded</Typography>
                  ) : (
                    <Typography variant="subtitle2">
                      {payment_status || '-'}
                    </Typography>
                  )}
                </Box>
                <Box>
                  {(isFrontdesk || isFinance) &&
                    (payment_status === 'paid' ||
                      (payment_status === 'unpaid' &&
                        appointment_status !== 'cancel')) && (
                      <FrontdeskButton
                        title={
                          payment_status === 'paid'
                            ? 'Payment details'
                            : 'Take payment'
                        }
                        className="take-payment-btn"
                        onClick={handlePaymentDetailsClick}
                      />
                    )}
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Grid>
  );
};

export default FinancialDetails;
